import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';

/** The table's search box matches any text in a row — a field name, a reading or a raw value. */
export function searchRows(rows, search) {
    if (!search) return rows;
    const term = search.toLowerCase();

    return rows.filter((row) =>
        Object.values(row).some(
            (value) => typeof value === 'string' && value.toLowerCase().includes(term),
        ),
    );
}

/**
 * One numbered block of a report that reads records rather than metrics — URL Inspection, Sitemap
 * Status: a card holding a table of returned evidence, the notes that say how to read it, and the
 * shared table's own search and CSV/PDF export.
 *
 * Every block is the same shape because every block is the same kind of thing — fields of one
 * response, read one way — so blocks differ only in their rows and their words. The exports carry
 * the fetched time in the file name the report passes, so a downloaded block still says when
 * Google was asked.
 *
 * A block can add header `actions` (an export of its own), `children` under the table (a further
 * table the block expands into), and `tableProps` for the shared table's own flags — a record
 * filter as `additionalBox`, a picked row as `rowClassName` and `onRowClick` — rather than growing
 * a second kind of card.
 */
export default function EvidenceBlock({
    number,
    title,
    description,
    columns,
    rows,
    fileName,
    emptyMessage,
    banner,
    actions,
    children,
    tableProps,
    notes = [],
}) {
    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">
                        {number} · {title}
                    </p>
                    <p className="gsc-tool-sub">{description}</p>
                </div>

                {!!actions && <div className="gsc-tool-icon-actions">{actions}</div>}
            </div>

            <div className="card-body">
                {banner}

                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    searchFunction={searchRows}
                    fileName={fileName}
                    customEmptyMessage={emptyMessage}
                    customContainerStyles="gsc-tool-key-table"
                    {...tableProps}
                />

                {children}

                {notes.map((note) => (
                    <p key={note} className="gsc-tool-table-note">
                        {note}
                    </p>
                ))}
            </div>
        </div>
    );
}
